import axios, { AxiosInstance } from "axios";

import {
  AnalysisRow,
  ApiResponse,
  AudinceCampaignOptionResponse,
  BusinessMetricsResponse,
  CampaignFilterRow,
  ClientRow,
  CPAClientsResponse,
  CPAResponse,
  CreateCampaignRequest,
  CreateExperimentRequest,
  CreateNoteRequest,
  EvidenceGatesCheck1Response,
  EvidenceGatesCheck2Response,
  Experiment,
  ExperimentAnalysisRunResponse,
  ExperimentAnalysisRunsResponse,
  ExperimentCampaign,
  ExperimentDataParams,
  ExperimentDataResponse,
  ExperimentDiagnosticsResponse,
  ExperimentDiagnosticsRunResponse,
  ExperimentNote,
  ExperimentsDailyDeliveryParams,
  ExperimentsDailyDeliveryResponse,
  ExperimentsListParams,
  ExperimentsListResponse,
  ExperimentTagsListParams,
  ExperimentTagsResponse,
  ExperimentTimelineParams,
  ExperimentTimelineResponse,
  ExperimentTimelineSummaryParams,
  ExperimentTimelineSummaryResponse,
  ExperimentVersionsResponse,
  ExperimentVertexRunsResponse,
  GlobalExperimentImportResponse,
  GlobalExperimentOptionsResponse,
  JiraMetricsProjectsResponse,
  JiraMetricsSummaryParams,
  JiraMetricsSummaryResponse,
  JiraMetricsSyncRunsResponse,
  JiraMetricsTicketParams,
  JiraMetricsTicketsResponse,
  NotificationsResponse,
  PerformanceResponse,
  PredictionFlightsResponse,
  RevenueHistoryRow,
  RevenueOverviewRow,
  ScheduleExperimentAnalysisRequest,
  SetExperimentRolloutDurabilityRequest,
  SetExperimentRolloutRequest,
  SetExperimentTagsRequest,
  StabilityResponse,
  TeamGoalsSummaryResponse,
  UpdateExperimentRequest,
  UpdateNoteRequest,
  User,
  VertexMonitoredModelsResponse,
} from "./api-types";
import { ensureAuthenticatedRequest } from "./auth";
import { config } from "./config";

export const dashboardApi: AxiosInstance = axios.create({
  baseURL: config().dashboardBaseUrl,
  headers: { "Content-Type": "application/json" },
});

dashboardApi.interceptors.request.use(ensureAuthenticatedRequest);

type Props = {
  method: "post" | "get" | "patch" | "put" | "delete";
  url: string;
  data?: object;
  params?: object;
};

// The fetch function uses the api axios instance
export function fetch<T>({ method, url, data, params }: Props): Promise<T> {
  return dashboardApi({
    method,
    url,
    data,
    params,
  }).then(res => res.data);
}

async function getRevenueHistory(url: string, params: object): Promise<RevenueHistoryRow[]> {
  return fetch({ method: "get", url, params });
}

async function getAudienceAndCampaignOptions(url: string, params: object): Promise<AudinceCampaignOptionResponse> {
  return fetch({ method: "get", url, params });
}

async function getPerformanceData(url: string, params: object): Promise<PerformanceResponse> {
  return fetch({ method: "get", url, params });
}

async function getExperimentData(url: string, params: ExperimentDataParams): Promise<ExperimentDataResponse> {
  return fetch({ method: "get", url, params });
}

async function getExperimentsDailyDelivery(
  url: string,
  params: ExperimentsDailyDeliveryParams,
): Promise<ExperimentsDailyDeliveryResponse> {
  return fetch({ method: "get", url, params });
}

async function getExperimentTimelineSummary(
  url: string,
  params: ExperimentTimelineSummaryParams,
): Promise<ExperimentTimelineSummaryResponse> {
  return fetch({ method: "get", url, params });
}

async function getExperimentTimeline(
  url: string,
  params: ExperimentTimelineParams,
): Promise<ExperimentTimelineResponse> {
  return fetch({ method: "get", url, params });
}

async function listExperiments(url: string, params: ExperimentsListParams): Promise<ExperimentsListResponse> {
  return fetch({ method: "get", url, params });
}

async function getExperiment(url: string): Promise<Experiment> {
  return fetch({ method: "get", url });
}

async function listExperimentVersions(url: string): Promise<ExperimentVersionsResponse> {
  return fetch({ method: "get", url });
}

async function getExperimentDiagnostics(url: string): Promise<ExperimentDiagnosticsResponse> {
  return fetch({ method: "get", url });
}

async function acknowledgeExperimentDiagnostic(url: string): Promise<{ data: string }> {
  return fetch({ method: "post", url });
}

async function acknowledgeExperimentDiagnosticDailySpend(url: string): Promise<{ data: string }> {
  return fetch({ method: "post", url });
}

async function runExperimentDiagnostics(url: string): Promise<ExperimentDiagnosticsRunResponse> {
  return fetch({ method: "post", url });
}

async function getStability(url: string): Promise<StabilityResponse> {
  return fetch({ method: "get", url });
}

async function getExperimentVertexRuns(url: string): Promise<ExperimentVertexRunsResponse> {
  return fetch({ method: "get", url });
}

async function getVertexMonitoredModels(url: string): Promise<VertexMonitoredModelsResponse> {
  return fetch({ method: "get", url });
}

async function scheduleExperimentAnalysisRun(
  url: string,
  data: ScheduleExperimentAnalysisRequest,
): Promise<ExperimentAnalysisRunResponse> {
  return fetch({ method: "post", url, data });
}

async function getExperimentAnalysisRun(url: string): Promise<ExperimentAnalysisRunResponse> {
  return fetch({ method: "get", url });
}

async function listExperimentAnalysisRuns(url: string): Promise<ExperimentAnalysisRunsResponse> {
  return fetch({ method: "get", url });
}

async function createExperiment(url: string, data: CreateExperimentRequest): Promise<Experiment> {
  return fetch({ method: "post", url, data });
}

async function updateExperiment(url: string, data: UpdateExperimentRequest): Promise<Experiment> {
  return fetch({ method: "put", url, data });
}

async function setExperimentRollout(url: string, data: SetExperimentRolloutRequest): Promise<Experiment> {
  return fetch({ method: "post", url, data });
}

async function setExperimentRolloutDurability(
  url: string,
  data: SetExperimentRolloutDurabilityRequest,
): Promise<Experiment> {
  return fetch({ method: "post", url, data });
}

async function deleteExperiment(url: string): Promise<{ data: string }> {
  return fetch({ method: "delete", url });
}

// Users API
async function listUsers(url: string): Promise<{ data: User[] }> {
  return fetch({ method: "get", url });
}

async function getUser(url: string): Promise<{ user: User }> {
  return fetch({ method: "get", url });
}

// Experiment Campaigns API
async function listExperimentCampaigns(url: string): Promise<{ data: ExperimentCampaign[] }> {
  return fetch({ method: "get", url });
}

async function createExperimentCampaign(url: string, data: CreateCampaignRequest): Promise<ExperimentCampaign> {
  return fetch({ method: "post", url, data });
}

async function deleteExperimentCampaign(url: string): Promise<{ data: string }> {
  return fetch({ method: "delete", url });
}

// Experiment Notes API
async function listExperimentNotes(url: string): Promise<{ data: ExperimentNote[] }> {
  return fetch({ method: "get", url });
}

async function createExperimentNote(url: string, data: CreateNoteRequest): Promise<ExperimentNote> {
  return fetch({ method: "post", url, data });
}

async function updateExperimentNote(url: string, data: UpdateNoteRequest): Promise<ExperimentNote> {
  return fetch({ method: "put", url, data });
}

async function deleteExperimentNote(url: string): Promise<{ data: string }> {
  return fetch({ method: "delete", url });
}

// Experiment Tags API
async function listExperimentTags(url: string, params: ExperimentTagsListParams): Promise<ExperimentTagsResponse> {
  return fetch({ method: "get", url, params });
}

async function setExperimentTags(url: string, data: SetExperimentTagsRequest): Promise<ExperimentTagsResponse> {
  return fetch({ method: "put", url, data });
}

// Experiment Images API
async function uploadImage(formData: FormData, experimentId: string): Promise<{ url: string }> {
  return dashboardApi
    .post(`/api/experiments/${experimentId}/images`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(res => res.data);
}

async function getRevenueOverview(url: string): Promise<ApiResponse<RevenueOverviewRow>> {
  return fetch({ method: "get", url });
}

async function getClients(url: string): Promise<ApiResponse<ClientRow>> {
  return fetch({ method: "get", url });
}

async function getAnalysis(url: string, params: object): Promise<ApiResponse<AnalysisRow>> {
  return fetch({ method: "get", url, params });
}

async function getCampaignFilter(
  url: string,
  params: object,
): Promise<{ minute: CampaignFilterRow[]; all: CampaignFilterRow[] }> {
  return fetch({ method: "get", url, params });
}

async function getBusinessMetrics(url: string): Promise<BusinessMetricsResponse> {
  return fetch({ method: "get", url });
}

// Prediction Flights API
async function getPredictionFlightOptions(url: string): Promise<PredictionFlightsResponse> {
  return fetch({ method: "get", url });
}

async function getGlobalExperimentOptions(url: string): Promise<GlobalExperimentOptionsResponse> {
  return fetch({ method: "get", url });
}

async function getGlobalExperimentImport(url: string): Promise<GlobalExperimentImportResponse> {
  return fetch({ method: "get", url });
}

async function getJiraMetricsProjects(url: string): Promise<JiraMetricsProjectsResponse> {
  return fetch({ method: "get", url });
}

async function getJiraMetricsSummary(
  url: string,
  params: JiraMetricsSummaryParams,
): Promise<JiraMetricsSummaryResponse> {
  return fetch({ method: "get", url, params });
}

async function getJiraMetricsTickets(
  url: string,
  params: JiraMetricsTicketParams,
): Promise<JiraMetricsTicketsResponse> {
  return fetch({ method: "get", url, params });
}

async function getJiraMetricsSyncRuns(url: string, params: { limit?: number }): Promise<JiraMetricsSyncRunsResponse> {
  return fetch({ method: "get", url, params });
}

async function getEvidenceGatesCheck1(url: string): Promise<EvidenceGatesCheck1Response> {
  return fetch({ method: "get", url });
}

async function getEvidenceGatesCheck2(url: string): Promise<EvidenceGatesCheck2Response> {
  return fetch({ method: "get", url });
}

async function getTeamGoalsSummary(url: string): Promise<TeamGoalsSummaryResponse> {
  return fetch({ method: "get", url });
}

async function getTeamGoalsCPAClients(url: string): Promise<CPAClientsResponse> {
  return fetch({ method: "get", url });
}

async function getTeamGoalsCPA(url: string, params: { org_ids: string }): Promise<CPAResponse> {
  return fetch({ method: "get", url, params });
}

async function listNotifications(url: string): Promise<NotificationsResponse> {
  return fetch({ method: "get", url });
}

async function getUnreadNotificationCount(url: string): Promise<{ unread_count: number }> {
  return fetch({ method: "get", url });
}

async function markNotificationRead(url: string): Promise<{ data: string }> {
  return fetch({ method: "post", url });
}

async function markAllNotificationsRead(url: string): Promise<{ data: string }> {
  return fetch({ method: "post", url });
}

export {
  acknowledgeExperimentDiagnostic,
  acknowledgeExperimentDiagnosticDailySpend,
  createExperiment,
  createExperimentCampaign,
  createExperimentNote,
  deleteExperiment,
  deleteExperimentCampaign,
  deleteExperimentNote,
  getAnalysis,
  getAudienceAndCampaignOptions,
  getBusinessMetrics,
  getCampaignFilter,
  getClients,
  getEvidenceGatesCheck1,
  getEvidenceGatesCheck2,
  getExperiment,
  getExperimentAnalysisRun,
  getExperimentData,
  getExperimentDiagnostics,
  getExperimentsDailyDelivery,
  getExperimentTimeline,
  getExperimentTimelineSummary,
  getExperimentVertexRuns,
  getGlobalExperimentImport,
  getGlobalExperimentOptions,
  getJiraMetricsProjects,
  getJiraMetricsSummary,
  getJiraMetricsSyncRuns,
  getJiraMetricsTickets,
  getPerformanceData,
  getPredictionFlightOptions,
  getRevenueHistory,
  getRevenueOverview,
  getStability,
  getTeamGoalsCPA,
  getTeamGoalsCPAClients,
  getTeamGoalsSummary,
  getUnreadNotificationCount,
  getUser,
  getVertexMonitoredModels,
  listExperimentAnalysisRuns,
  listExperimentCampaigns,
  listExperimentNotes,
  listExperiments,
  listExperimentTags,
  listExperimentVersions,
  listNotifications,
  listUsers,
  markAllNotificationsRead,
  markNotificationRead,
  runExperimentDiagnostics,
  scheduleExperimentAnalysisRun,
  setExperimentRollout,
  setExperimentRolloutDurability,
  setExperimentTags,
  updateExperiment,
  updateExperimentNote,
  uploadImage,
};
